<template>
	<div
		v-if="lifeStageData && filteredCategories && savings && debts"
		:class="['calendar-page', 'card-page', { tutorial: showCalendarTutorial }]"
	>
		<ja-header
			:title="$t('gamePlayMetaTitles.calendar')"
			:subtitle="$t('dashboardShoppingSubtitle')"
			:show-home="!showCalendarTutorial"
			:show-intro="!showCalendarTutorial"
		>
			<template v-slot:icon>
				<svgicon name="headerIcons/calendar"></svgicon>
			</template>
		</ja-header>
		<div class="content-wrapper">
			<content
				ref="pageContent"
				:class="{
					'space-top': showCalendarTutorial
				}"
			>
				<ja-calendar-container
					ref="savings"
					:title="$t('savingsTitle')"
					:subtitle="$t('savingsSubtitle')"
					:amount-spent="savingsExpenses"
					:show-onboarding="showCalendarTutorial && currentOnboardingStep == 2"
					@continueOnboarding="continueOnboarding"
				>
					<div class="calendar-dashboard" role="list">
						<ja-dashboard-item
							v-for="item in savings"
							:key="item.studentGoalId"
							:item="item"
							class="dashboard-item"
							role="listitem"
						>
							<template v-slot:balance-info>
								{{ $t('accountBalance') }}: {{ item.accountBalance | currency }}
							</template>
							<template v-slot:icon>
								<svgicon class="done" name="done" aria-hidden="true"></svgicon>
							</template>
						</ja-dashboard-item>
						<ja-tooltip
							v-if="showCalendarTutorial"
							v-show="currentOnboardingStep == 1"
							class="tooltip"
							:pointer="false"
							tabindex="0"
							aria-activedescendant="text-tooltip-1"
						>
							<h1 id="text-tooltip-1" class="body-text-1">
								{{ $t('calendarОnboardingGeneral') }}
							</h1>
							<ja-button
								class="small transparent"
								name="drag-help"
								:aria-label="$t('goNextOnboardingStep')"
								@click.native="continueOnboarding"
								>{{ $t('next') }}
							</ja-button>
						</ja-tooltip>
						<ja-tooltip
							v-if="showCalendarTutorial"
							v-show="currentOnboardingStep == 4"
							ref="tooltip"
							class="tooltip"
							:pointer="false"
							tabindex="0"
							aria-activedescendant="text-tooltip-4"
						>
							<h1 id="text-tooltip-4" class="body-text-1">
								{{ $t('calendarОnboardingConclusion') }}
							</h1>
							<ja-button
								class="small transparent"
								name="drag-help"
								:aria-label="$t('finishCalendarTutorial')"
								@click.native="finishCalendarOnboarding"
								>{{ $t('finish') }}</ja-button
							>
						</ja-tooltip>
					</div>
				</ja-calendar-container>
				<ja-calendar-container
					v-if="!lifeStageIsTeenager"
					:title="$t('debtTitle')"
					:subtitle="$t('debtSubtitle')"
					:amount-spent="debtsExpenses"
				>
					<div class="calendar-dashboard" role="list">
						<ja-dashboard-item
							:item="debts.creditCard"
							class="dashboard-item"
							role="listitem"
						>
							<template v-slot:balance-info>
								<span class="credit-balance" name="remainingBalance"
									>{{ $t('remainingBalance') }}:
									{{ debts.creditCard.balance | currency }}</span
								><span class="credit-limit" name="creditLimit"
									>{{ $t('creditLimit') }}:
									{{ debts.creditCard.creditLimit | currency }}</span
								>
							</template>
							<template v-slot:icon>
								<button
									class="tooltip-btn"
									:aria-label="$t('moreInfoCreditCard')"
									@click="toggleDebtTooltip('creditCard')"
									@mousedown.prevent
								>
									<svgicon
										class="info"
										name="info"
										aria-hidden="true"
									></svgicon>
								</button>
								<ja-tooltip
									v-show="showCreditCardTooltip"
									class="debt-tooltip right"
									><div class="body-text-1">
										{{ $t('debtTooltip', { typeOfDebt: 'credit card' }) }}
									</div>
									<ja-button
										class="small transparent"
										name="credit-done"
										@click.native="toggleDebtTooltip('creditCard')"
										>{{ $t('okay') }}</ja-button
									></ja-tooltip
								>
							</template>
						</ja-dashboard-item>
						<ja-dashboard-item
							v-if="!educationIsFree"
							ref="categoriesDashboard"
							:item="debts.educationLoan"
							class="dashboard-item"
							role="listitem"
						>
							<template v-slot:balance-info
								>{{ $t('remainingBalance') }}:
								{{ debts.educationLoan.remainingBalance | currency }}/{{
									debts.educationLoan.totalBalance | currency
								}}
							</template>
							<template v-slot:icon>
								<button
									class="tooltip-btn"
									:aria-label="$t('moreInfoPostLoan')"
									@click="toggleDebtTooltip('educationLoan')"
									@mousedown.prevent
								>
									<svgicon
										class="info"
										name="info"
										aria-hidden="true"
									></svgicon>
								</button>
								<ja-tooltip
									v-show="showEducationLoanTooltip"
									class="debt-tooltip right"
									><div class="body-text-1">
										{{ $t('debtTooltip', { typeOfDebt: 'education loan' }) }}
									</div>
									<ja-button
										class="small transparent"
										name="education-done"
										@click.native="toggleDebtTooltip('educationLoan')"
										>{{ $t('okay') }}</ja-button
									>
								</ja-tooltip>
							</template>
						</ja-dashboard-item>
					</div>
				</ja-calendar-container>
				<ja-calendar-container
					:title="$t('expensesTitle')"
					:subtitle="$t('expensesSubtitle')"
					:amount-spent="categoriesExpenses"
					:class="[
						'expense-card-wrapper',
						{ 'space-bottom': showCalendarTutorial }
					]"
					role="list"
				>
					<ja-expense-card
						v-for="(category, index) in filteredCategories"
						:key="category.expenseCategory_ID"
						class="calendar-card"
						:category="category"
						:show-tutorial="
							showCalendarTutorial && currentOnboardingStep == 3 && index == 0
						"
						@continueOnboarding="continueOnboarding"
					></ja-expense-card>
				</ja-calendar-container>
			</content>
			<footer
				:class="{
					absolute: showCalendarTutorial
				}"
			>
				<ja-chat-bot
					v-if="userData.userActiveSimulationAIChatbotEnable"
					:is-chat-in-footer="true"
					:hide-chatbot-button="$store.state.loading"
				/>
				<div class="footer-item">
					<ja-national-sponsor></ja-national-sponsor>
				</div>
				<div class="footer-item">
					<ja-budget-counter></ja-budget-counter>
				</div>
				<div class="footer-item">
					<ja-financial-health
						v-if="!profileData.isEntryVirtualSimulationType"
					></ja-financial-health>
				</div>
				<div class="footer-item">
					<ja-button
						class="small"
						name="close-calendar"
						:aria-hidden="showCalendarTutorial"
						:aria-label="$t('closeCalendar')"
						:tabindex="showCalendarTutorial ? -1 : 0"
						@click.native.once="closeCalendar"
						>{{ $t('close') }}</ja-button
					>
				</div>
			</footer>
			<div v-if="showCalendarTutorial" class="onboarding-wrapper"></div>
		</div>
	</div>
</template>
<script>
import '../components/icons/done';
import '../components/icons/info';
import '../components/icons/expenseCategories';
import '../components/icons/headerIcons/calendar';
import LifeStageData from '../mixins/LifeStageData';
import ProfileData from '../mixins/ProfileData';
import ExpenseCategories from '../mixins/ExpenseCategories';
import Header from '../components/ui/headers/Header';
import Button from '../components/ui/Button';
import Tooltip from '../components/ui/Tooltip';
import NationalSponsor from '../components/ui/NationalSponsor';
import CalendarContainer from '../components/Calendar/CalendarContainer';
import DashboardItem from '../components/Calendar/DashboardItem';
import ExpenseCard from '../components/Calendar/ExpenseCard';
import BudgetCounter from '../components/Metrics/BudgetCounter';
import FinancialHealth from '../components/Metrics/FinancialHealthMeter';
import ChatBot from '@/components/ChatBot/ChatBot';

export default {
	components: {
		jaHeader: Header,
		jaCalendarContainer: CalendarContainer,
		jaDashboardItem: DashboardItem,
		jaExpenseCard: ExpenseCard,
		jaBudgetCounter: BudgetCounter,
		jaFinancialHealth: FinancialHealth,
		jaButton: Button,
		jaTooltip: Tooltip,
		jaNationalSponsor: NationalSponsor,
		jaChatBot: ChatBot
	},
	mixins: [LifeStageData, ExpenseCategories, ProfileData],
	data() {
		return {
			showCreditCardTooltip: false,
			showEducationLoanTooltip: false,
			currentOnboardingStep: 1
		};
	},
	computed: {
		savings() {
			return this.$store.getters.savings;
		},
		debts() {
			return this.$store.getters.debts;
		},
		categoriesExpenses() {
			if (this.filteredCategories) {
				return this.filteredCategories.reduce((sum, item) => {
					if (item.amountSpentForCurrentStudent >= 0) {
						return sum + parseInt(item.amountSpentForCurrentStudent);
					}
				}, 0);
			}

			return 0;
		},
		savingsExpenses() {
			if (this.savings) {
				return this.savings.reduce((sum, item) => {
					if (item.amount >= 0) {
						return sum + parseInt(item.amount);
					}
				}, 0);
			}

			return 0;
		},
		debtsExpenses() {
			if (this.debts) {
				return this.debts.creditCard.amount + this.debts.educationLoan.amount;
			}

			return 0;
		},
		showCalendarTutorial() {
			return !this.userData || !this.userData.hideCalendarTutorialPopup;
		},
		userData() {
			return this.$store.getters.userData;
		}
	},

	watch: {
		filteredCategories() {
			this.$store.commit('setLoading', false);
		}
	},
	updated() {
		if (
			this.showCalendarTutorial &&
			this.currentOnboardingStep == 4 &&
			this.$refs.tooltip
		) {
			this.$refs.tooltip.$el.focus();
		}
	},
	created() {
		this.checkForLifeStageData();
		this.checkForProfileData();
		this.checkForCategories();
	},
	beforeCreate() {
		this.$store.commit('setLoading', true);
	},
	mounted() {
		this.$store.commit('setLoading', false);

		if (this.showCalendarTutorial) {
			window.addEventListener('resize', this.scrollToElements);
		}
	},
	beforeDestroy() {
		if (this.showCalendarTutorial) {
			window.removeEventListener('resize', this.scrollToElements);
		}
	},

	methods: {
		scrollToElements() {
			if (this.currentOnboardingStep == 3) {
				this.scrollToExpenseCategories();
			}
			if (this.currentOnboardingStep == 4) {
				this.scrollToSavings();
			}
		},
		closeCalendar() {
			this.$router.push({ name: 'home' });
		},
		continueOnboarding() {
			this.currentOnboardingStep++;
			if (this.currentOnboardingStep == 3) {
				this.scrollToExpenseCategories();
			}
			if (this.currentOnboardingStep == 4) {
				this.scrollToSavings();
			}
		},
		toggleDebtTooltip(debtName) {
			if (debtName == 'creditCard') {
				this.showEducationLoanTooltip = false;
				this.showCreditCardTooltip = !this.showCreditCardTooltip;
				if (this.showCreditCardTooltip) {
					this.$announcer.polite(
						this.$t('debtTooltip', { typeOfDebt: 'credit card' })
					);
				}
			} else {
				this.showCreditCardTooltip = false;
				this.showEducationLoanTooltip = !this.showEducationLoanTooltip;
				if (this.showEducationLoanTooltip) {
					this.$announcer.polite(
						this.$t('debtTooltip', { typeOfDebt: 'education loan' })
					);
				}
			}
		},
		scrollToExpenseCategories() {
			if (this.$refs.categoriesDashboard)
				this.$refs.categoriesDashboard.$el.scrollIntoView({
					block: 'start'
				});
			else {
				this.currentOnboardingStep++;
			}
		},
		scrollToSavings() {
			this.$refs.savings.$el.scrollIntoView({
				block: 'center'
			});
		},
		finishCalendarOnboarding() {
			this.$router.push({
				name: 'home',
				params: { isMapOnboarding: true }
			});
		}
	}
};
</script>
<style lang="scss" scoped>
.calendar-page {
	&.tutorial {
		.content-wrapper {
			content {
				overflow-y: hidden;
			}
		}
	}
	.content-wrapper {
		position: relative;
		width: 100%;
		display: flex;
		flex-direction: column;
		height: calc(100vh - 100px);

		.onboarding-wrapper {
			width: 100%;
			height: 100%;
			position: absolute;
			top: 0;
			left: 0;
			z-index: 9;
			background-color: rgba(54, 62, 82, 0.6);
		}

		content {
			padding: 32px 5%;
			margin: auto;

			&.space-top {
				padding-top: 48px;
			}

			.calendar-container {
				max-width: 850px;
				margin: auto;

				.tooltip {
					transform: translateZ(1px);
					-webkit-transform: translateZ(1px);
					z-index: 999;
					width: 300px;
					text-align: center;
					padding: 24px 24px 16px 24px;
					left: 0;
					right: 0;
					top: 0;
					margin: auto;
					outline: 0;

					h1 {
						margin: 0;
					}

					@media (min-width: $breakpoint-mid) {
						width: 360px;
					}

					button {
						height: auto;
						margin-top: 24px;
					}
				}

				.calendar-dashboard {
					position: relative;
					width: 100%;
					background-color: #ffffff;
					border-radius: 6px;

					.dashboard-item {
						&:not(:last-child) {
							border-bottom: 1px solid rgba(220, 220, 220, 0.44);
						}
						.credit-balance {
							padding-right: 8px;
							margin-right: 8px;
							border-right: 1px solid $font-dark;
						}
						.debt-tooltip {
							top: 10px;
							right: 64px;
							width: 320px;
							text-align: center;
							transform: translateY(-50%);
							padding: 32px 32px 24px 32px;

							button {
								height: auto;
								margin-top: 24px;
							}
						}

						.tooltip-btn {
							padding: 0;
							background-color: transparent;
							border: 0;
						}
						svg {
							height: 20px;
							width: 20px;

							&.info {
								fill: $primary-state;

								&:hover {
									cursor: pointer;
									fill: $hover-state;
								}
							}
							&.done {
								fill: $icon-read;
							}
						}
					}
				}

				&.expense-card-wrapper {
					&.space-bottom {
						margin-bottom: 128px;
					}
					::v-deep content {
						padding-right: 0;
					}
				}

				&:not(:last-child) {
					margin-bottom: 16px;
				}

				.calendar-card {
					width: calc(25% - 8px);
					margin-bottom: 8px;
					margin-right: 8px;
				}
			}
		}

		footer {
			height: 90px;

			&.absolute {
				position: absolute;
				bottom: 0;
			}

			.footer-item {
				width: 25%;

				&:last-child {
					text-align: right;
				}

				.footer-text {
					color: $font-dark;
					font-family: 'RobotoRegular';
					font-size: 12px;

					@media (min-width: $breakpoint-mid) {
						font-size: 14px;
					}
				}

				img {
					display: block;
					max-height: 48px;
					margin-top: 4px;
				}
			}
		}
	}
}
</style>
